import React from "react";
import {Wod, WodType, TimeCapResult, AmrapResult, WeightResult, MaxTimeResult, WodCat, ScreenType, WodDescription, Heat, Ranking} from "./types";

interface ScreenProps {
    screen: ScreenType;
}

// Type guard to check if data is a Wod
const isWod = (data: Wod | WodDescription | Heat | Ranking): data is Wod => {
    return (data as Wod).results !== undefined && (data as Wod).type !== undefined;
};

// Type guard to check if data is a Heat
const isHeat = (data: Wod | WodDescription | Heat | Ranking): data is Heat => {
    return (data as Heat).teams !== undefined;
};

// Type guard to check if data is a Ranking
const isRanking = (data: Wod | WodDescription | Heat | Ranking): data is Ranking => {
    return (data as Ranking).result !== undefined && Array.isArray((data as Ranking).result) &&
           (data as Ranking).result.length > 0 && (data as Ranking).result[0].team !== undefined;
};

const Screen: React.FC<ScreenProps> = ({ screen }) => {
    console.debug('[Screen] render received screen:', screen);
    const formatTime = (mins?: number, seconds?: number): string => {
        const m = mins || 0;
        const s = seconds || 0;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const formatResult = (result: any, type: WodType): string => {
        switch (type) {
            case WodType.TIMECAP:
                const tcResult = result as TimeCapResult;
                if (tcResult.cap) {
                    return `CAP + ${tcResult.reps || 0} reps`;
                }
                return formatTime(tcResult.mins, tcResult.seconds);
            case WodType.AMRAP:
                const amrapResult = result as AmrapResult;
                return `${amrapResult.reps} reps`;
            case WodType.WEIGHT:
                const weightResult = result as WeightResult;
                return `${weightResult.weight} lbs`;
            case WodType.MAXTIME:
                const maxTimeResult = result as MaxTimeResult;
                return formatTime(maxTimeResult.mins, maxTimeResult.seconds);
            default:
                return '';
        }
    };

    const getRankingColor = (ranking: number): string => {
        if (ranking === 1) return 'bg-black bg-opacity-60 border-2 border-emerald-400';
        if (ranking === 2) return 'bg-black bg-opacity-50 border-2 border-emerald-300';
        if (ranking === 3) return 'bg-black bg-opacity-50 border-2 border-emerald-200';
        return 'bg-black bg-opacity-40 border border-gray-600';
    };

    // Check if data is a Wod, Heat or WodDescription
    if (isWod(screen.data)) {
        console.debug('[Screen] rendering WOD:', (screen.data as any).name);
        const wod = screen.data;
        const sortedResults = [...wod.results].sort((a, b) => a.ranking - b.ranking);

        // Split into two columns if there are more than 6 teams
        const shouldSplit = sortedResults.length > 6;
        const midPoint = Math.ceil(sortedResults.length / 2);
        const leftColumn = shouldSplit ? sortedResults.slice(0, midPoint) : sortedResults;
        const rightColumn = shouldSplit ? sortedResults.slice(midPoint) : [];

        const renderTeamResult = (teamResult: any, index: number) => (
            <div
                key={index}
                className={`${getRankingColor(teamResult.ranking)} rounded-lg backdrop-blur-sm transition-all hover:bg-opacity-70`}
            >
                <div className="grid grid-cols-12 gap-3 px-4 py-4 items-center">
                    {/* Ranking Number */}
                    <div className="col-span-2 text-center">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${
                            teamResult.ranking === 1 ? 'bg-emerald-400 text-black' :
                            teamResult.ranking === 2 ? 'bg-emerald-300 text-black' :
                            teamResult.ranking === 3 ? 'bg-emerald-200 text-black' :
                            'bg-white bg-opacity-10 text-white'
                        }`}>
                            <span className="text-xl font-bold">{teamResult.ranking}</span>
                        </div>
                    </div>

                    {/* Team Names */}
                    <div className="col-span-5">
                        <div className="font-bold text-lg text-white tracking-wide">
                            {teamResult.team.athlete1.toUpperCase()}
                        </div>
                        <div className="font-bold text-lg text-white tracking-wide">
                            {teamResult.team.athlete2.toUpperCase()}
                        </div>
                    </div>

                    {/* Result/Time */}
                    <div className="col-span-3 text-right">
                        <span className="text-lg font-bold text-white">
                            {formatResult(teamResult.result, wod.type)}
                        </span>
                    </div>

                    {/* Points */}
                    <div className="col-span-2 text-right">
                        <div className="inline-flex flex-col items-end">
                            <span className="text-2xl font-bold text-white">
                                {teamResult.points}
                            </span>
                            <span className="text-xs text-gray-400">pts</span>
                        </div>
                    </div>
                </div>
            </div>
        );

        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-6 pb-12">
                <div className="max-w-[95vw] mx-auto">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <h1 className="text-4xl font-bold mb-2 tracking-tight">{wod.name}</h1>
                        <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold tracking-wider ${
                            wod.category === WodCat.SCALE
                                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50'
                                : 'bg-orange-500 text-white shadow-lg shadow-orange-500/50'
                        }`}>
                            {WodCat[wod.category]}
                        </span>
                    </div>

                    {/* Rankings Table - Single or Two Columns */}
                    {shouldSplit ? (
                        <div className="grid grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-3">
                                {leftColumn.map((teamResult, index) => renderTeamResult(teamResult, index))}
                            </div>
                            {/* Right Column */}
                            <div className="space-y-3">
                                {rightColumn.map((teamResult, index) => renderTeamResult(teamResult, midPoint + index))}
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-5xl mx-auto space-y-3">
                            {leftColumn.map((teamResult, index) => renderTeamResult(teamResult, index))}
                        </div>
                    )}
                </div>
            </div>
        );
    } else if (isHeat(screen.data)) {
        console.debug('[Screen] rendering Heat:', (screen.data as any).name);
        // Render Heat
        const heat = screen.data;

        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-8 pb-16">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-5xl font-bold mb-2 tracking-tight">{heat.name} - Heat {heat.number}</h1>
                        <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold tracking-wider ${
                            heat.category === WodCat.SCALE
                                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50'
                                : 'bg-orange-500 text-white shadow-lg shadow-orange-500/50'
                        }`}>
                            {WodCat[heat.category]}
                        </span>
                    </div>

                    {/* Teams List - Banner Style */}
                    <div className="flex flex-col gap-4">
                        {heat.teams.length === 0 && (
                            <div className="text-center text-gray-400">No teams assigned to this heat.</div>
                        )}

                        {heat.teams.map((team, idx) => {
                            // Check if this lane is a different category
                            const isDifferentCategory = heat.differentCategoryIndex?.includes(idx) || false;
                            const oppositeCategory = heat.category === WodCat.SCALE ? WodCat.RX : WodCat.SCALE;

                            return (
                                <div
                                    key={idx}
                                    className="bg-black bg-opacity-40 border border-gray-600 rounded-lg backdrop-blur-sm transition-all hover:bg-opacity-50"
                                    role="group"
                                    aria-label={`Lane ${idx + 1}: ${team.athlete1} & ${team.athlete2}`}
                                >
                                    <div className="flex items-center px-8 py-6">
                                        {/* Left: Lane Number */}
                                        <div className="flex items-center gap-6 flex-shrink-0">
                                            <div className="text-left">
                                                <div className="text-6xl md:text-7xl font-black text-white leading-none tracking-tight">
                                                    {idx + 1}
                                                </div>
                                                <div className="text-xs md:text-sm font-bold text-white/80 uppercase tracking-widest mt-1">
                                                    Lane
                                                </div>
                                            </div>
                                        </div>

                                        {/* Center: Team Names */}
                                        <div className="flex-1 text-center px-8">
                                            <div className="flex items-center justify-center gap-4">
                                                <div className="text-2xl md:text-3xl lg:text-4xl font-black text-white uppercase tracking-wide">
                                                    {team.athlete1}
                                                </div>
                                                <div className="text-2xl md:text-3xl lg:text-4xl text-white/70 font-bold">
                                                    —
                                                </div>
                                                <div className="text-2xl md:text-3xl lg:text-4xl font-black text-white uppercase tracking-wide">
                                                    {team.athlete2}
                                                </div>
                                            </div>

                                            {/* Different Category Badge */}
                                            {isDifferentCategory && (
                                                <div className="mt-3">
                                                    <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold tracking-wider ${
                                                        oppositeCategory === WodCat.SCALE
                                                            ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50'
                                                            : 'bg-orange-500 text-white shadow-lg shadow-orange-500/50'
                                                    }`}>
                                                        {WodCat[oppositeCategory]}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    } else if (isRanking(screen.data)) {
        console.debug('[Screen] rendering Ranking:', (screen.data as any).name);
        // Render Ranking (Overall standings)
        const ranking = screen.data;
        const sortedResults = [...ranking.result].sort((a, b) => a.ranking - b.ranking);

        // Split into two columns if there are more than 6 teams
        const shouldSplit = sortedResults.length > 6;
        const midPoint = Math.ceil(sortedResults.length / 2);
        const leftColumn = shouldSplit ? sortedResults.slice(0, midPoint) : sortedResults;
        const rightColumn = shouldSplit ? sortedResults.slice(midPoint) : [];

        const renderTeamCard = (teamRanking: any, index: number) => (
            <div
                key={index}
                className={`${getRankingColor(teamRanking.ranking)} rounded-lg backdrop-blur-sm transition-all hover:bg-opacity-70`}
            >
                {/* Main Row */}
                <div className="grid grid-cols-12 gap-3 px-4 py-4 items-center">
                    {/* Ranking Number */}
                    <div className="col-span-2 text-center">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${
                            teamRanking.ranking === 1 ? 'bg-emerald-400 text-black' :
                            teamRanking.ranking === 2 ? 'bg-emerald-300 text-black' :
                            teamRanking.ranking === 3 ? 'bg-emerald-200 text-black' :
                            'bg-white bg-opacity-10 text-white'
                        }`}>
                            <span className="text-xl font-bold">{teamRanking.ranking}</span>
                        </div>
                    </div>

                    {/* Team Names and Overview */}
                    <div className="col-span-8">
                        <div className="font-bold text-lg text-white tracking-wide">
                            {teamRanking.team.athlete1.toUpperCase()}
                        </div>
                        <div className="font-bold text-lg text-white tracking-wide mb-2">
                            {teamRanking.team.athlete2.toUpperCase()}
                        </div>

                        {/* Event Overview */}
                        {teamRanking.overview && teamRanking.overview.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {teamRanking.overview.map((eventResult: any, eventIdx: number) => (
                                    <div
                                        key={eventIdx}
                                        className="inline-flex items-center gap-1 bg-white bg-opacity-10 rounded-md px-2 py-1 border border-gray-500"
                                    >
                                        <span className="text-xs text-gray-400 font-semibold">W{eventIdx + 1}:</span>
                                        <span className={`text-xs font-bold ${
                                            eventResult.position === 1 ? 'text-emerald-400' :
                                            eventResult.position === 2 ? 'text-emerald-300' :
                                            eventResult.position === 3 ? 'text-emerald-200' :
                                            'text-white'
                                        }`}>
                                            #{eventResult.position}
                                        </span>
                                        <span className="text-xs text-gray-400">•</span>
                                        <span className="text-xs font-bold text-white">
                                            {eventResult.points}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Total Points */}
                    <div className="col-span-2 text-right">
                        <div className="inline-flex flex-col items-end">
                            <span className="text-2xl font-bold text-white">
                                {teamRanking.points}
                            </span>
                            <span className="text-xs text-gray-400">pts</span>
                        </div>
                    </div>
                </div>
            </div>
        );

        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-6 pb-12">
                <div className="max-w-[95vw] mx-auto">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <h1 className="text-4xl font-bold mb-2 tracking-tight">{ranking.name}</h1>
                        <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold tracking-wider ${
                            ranking.category === WodCat.SCALE
                                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50'
                                : 'bg-orange-500 text-white shadow-lg shadow-orange-500/50'
                        }`}>
                            {WodCat[ranking.category]}
                        </span>
                    </div>

                    {/* Rankings Table - Single or Two Columns */}
                    {shouldSplit ? (
                        <div className="grid grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-3">
                                {leftColumn.map((teamRanking, index) => renderTeamCard(teamRanking, index))}
                            </div>
                            {/* Right Column */}
                            <div className="space-y-3">
                                {rightColumn.map((teamRanking, index) => renderTeamCard(teamRanking, midPoint + index))}
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-5xl mx-auto space-y-3">
                            {leftColumn.map((teamRanking, index) => renderTeamCard(teamRanking, index))}
                        </div>
                    )}
                </div>
            </div>
        );
    } else {
        console.debug('[Screen] rendering WodDescription or other:', (screen.data as any).title || screen.data);
        // Render WodDescription
        const description = screen.data;

        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-8 pb-16">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-5xl font-bold mb-2 tracking-tight">{description.title}</h1>
                        <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold tracking-wider ${
                            description.category === WodCat.SCALE
                                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50'
                                : 'bg-orange-500 text-white shadow-lg shadow-orange-500/50'
                        }`}>
                            {WodCat[description.category]}
                        </span>
                        {description.subtitle && (
                            <h2 className="text-2xl font-semibold mt-4 text-gray-300">{description.subtitle}</h2>
                        )}
                    </div>

                    {/* Description Items */}
                    <div className="bg-black bg-opacity-40 border border-gray-600 rounded-lg backdrop-blur-sm p-8">
                        <div className="space-y-6">
                            {description.items.map((item, index) => {
                                // Check if item is <space> - render blank space
                                if (item.trim() === '<space>') {
                                    return <div key={index} className="h-4"></div>;
                                }

                                // Check if item starts with <h1>
                                if (item.startsWith('<h1>')) {
                                    const content = item.substring(4);
                                    return (
                                        <h1 key={index} className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
                                            {content}
                                        </h1>
                                    );
                                }

                                // Check if item starts with <h2>
                                if (item.startsWith('<h2>')) {
                                    const content = item.substring(4);
                                    return (
                                        <h2 key={index} className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                                            {content}
                                        </h2>
                                    );
                                }

                                // Check if item starts with <h3>
                                if (item.startsWith('<h3>')) {
                                    const content = item.substring(4);
                                    return (
                                        <h3 key={index} className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-snug">
                                            {content}
                                        </h3>
                                    );
                                }

                                // Check if item starts with <h4>
                                if (item.startsWith('<h4>')) {
                                    const content = item.substring(4);
                                    return (
                                        <h4 key={index} className="text-xl md:text-2xl lg:text-3xl font-semibold text-white leading-snug">
                                            {content}
                                        </h4>
                                    );
                                }

                                // Check if item starts with <b>
                                if (item.startsWith('<b>')) {
                                    const content = item.substring(3);
                                    return (
                                        <div key={index} className="text-2xl md:text-3xl lg:text-4xl font-black text-white leading-relaxed">
                                            {content}
                                        </div>
                                    );
                                }

                                // Check if item starts with a dot (.) - render with green bullet
                                if (item.startsWith('.')) {
                                    const content = item.substring(1).trim();
                                    return (
                                        <div key={index} className="flex items-start">
                                            <span className="inline-block w-3 h-3 bg-emerald-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span className="text-2xl md:text-3xl lg:text-4xl text-white leading-relaxed">{content}</span>
                                        </div>
                                    );
                                }

                                // Default rendering - no bullet
                                return (
                                    <div key={index} className="text-2xl md:text-3xl lg:text-4xl text-white leading-relaxed">
                                        {item}
                                    </div>
                                );
                            })}
                        </div>

                        {description.footer && (
                            <div className="mt-8 pt-6 border-t border-gray-600">
                                <p className="text-3xl text-gray-300 text-center font-semibold">{description.footer}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
};

export default Screen;

